if (!customElements.get('product-switcher')) {
    class ProductSwitcher extends HTMLElement {
        static SELECTORS = {
            step: '.switcher-step',
            option: '.switcher-option',
            selectedOption: '.switcher-option.is-selected'
        };
        static CSS = {
            selected: 'is-selected',
            hidden: 'hidden',
            configuratorMode: 'configurator-mode'
        };
        static ATTRS = {
            ariaHidden: 'aria-hidden',
            stepIndex: 'data-step-index'
        };
        static EVENTS = { select: 'switcher:select' };
        static STEPS = { gender: 1, style: 2 };
        static CONFIGURATOR_HASH = '#configurator';

        constructor () {
            super();
            this._isConfigurator =
                window.location.hash === ProductSwitcher.CONFIGURATOR_HASH ||
                document.body.classList.contains(ProductSwitcher.CSS.configuratorMode);
        }

        /* ─── Lifecycle ──────────────────────────────────────────────────────── */

        connectedCallback () {
            this._abort = new AbortController();
            this._steps = this.querySelectorAll(ProductSwitcher.SELECTORS.step);
            this._options = this.querySelectorAll(ProductSwitcher.SELECTORS.option);

            // Liquid pre-renders the correct is-selected + hidden state. JS only needs to
            // (re-)apply if Liquid didn't — which happens when there's no variant context
            // or the product changed via an AJAX re-render.
            this._ensureInitialSelectionState();
            this._ensureStep2Filter();

            if (this._isConfigurator) this._initConfiguratorMode();

            this._bindOptionClicks();
        }

        disconnectedCallback () {
            this._abort?.abort();
        }

        _bindOptionClicks () {
            this._options.forEach((opt) =>
                opt.addEventListener('click', (event) => this._onOptionClick(event), {
                    signal: this._abort.signal
                })
            );
        }

        /* ─── Initial state (idempotent — skips if Liquid already did it) ───── */

        // Liquid marks the Step 1/2 option that contains the current product. If it didn't
        // (e.g. no current product, or Liquid couldn't resolve it), fall back to JS.
        _ensureInitialSelectionState () {
            if (this.querySelector(ProductSwitcher.SELECTORS.selectedOption)) return;

            const currentProductId = this._currentProductId();
            if (!currentProductId) return;

            this._options.forEach((opt) => {
                if (this._optionProducts(opt).includes(currentProductId) || this._isRedirectTarget(opt)) {
                    opt.classList.add(ProductSwitcher.CSS.selected);
                }
            });
        }

        // Liquid may also pre-apply the filter (adds `hidden` + data-filtered-*). Run JS
        // filter only if Step 2 options look unfiltered (no hidden state and no data-filtered-*).
        _ensureStep2Filter () {
            const step2 = this._findStep(ProductSwitcher.STEPS.style);
            if (!step2) return;

            const anyFiltered = [...step2.querySelectorAll(ProductSwitcher.SELECTORS.option)]
                .some((opt) =>
                    opt.classList.contains(ProductSwitcher.CSS.hidden) ||
                    opt.dataset.filteredProducts !== undefined
                );
            if (anyFiltered) return;

            this._applyStep2Filter();
        }

        /* ─── Configurator mode ──────────────────────────────────────────────── */

        _initConfiguratorMode () {
            const step1 = this._findStep(ProductSwitcher.STEPS.gender);
            step1?.querySelectorAll(ProductSwitcher.SELECTORS.option)
                .forEach((opt) => opt.classList.remove(ProductSwitcher.CSS.selected));

            this._stepsAfter(ProductSwitcher.STEPS.gender).forEach((step) => {
                step.style.display = 'none';
            });
        }

        _revealHiddenBlocks () {
            document.body.classList.remove(ProductSwitcher.CSS.configuratorMode);
            document.documentElement.classList.remove(ProductSwitcher.CSS.configuratorMode);
        }

        _hideBlocks () {
            document.body.classList.add(ProductSwitcher.CSS.configuratorMode);
            document.documentElement.classList.add(ProductSwitcher.CSS.configuratorMode);
        }

        _revealNextSteps (fromStepIndex) {
            this._stepsAfter(fromStepIndex).forEach((step) => { step.style.display = ''; });
        }

        /* ─── Step 2 filtering (gender → style) ─────────────────────────────── */

        _applyStep2Filter () {
            const step1 = this._findStep(ProductSwitcher.STEPS.gender);
            const step2 = this._findStep(ProductSwitcher.STEPS.style);
            if (!step1 || !step2) return;

            const step2Options = step2.querySelectorAll(ProductSwitcher.SELECTORS.option);
            this._resetFilterState(step2Options);

            const selectedGenderOpt = step1.querySelector(ProductSwitcher.SELECTORS.selectedOption);
            if (!selectedGenderOpt) return;

            const allowedIds = this._optionProducts(selectedGenderOpt);
            step2Options.forEach((opt) => this._filterOptionAgainst(opt, allowedIds));
        }

        _resetFilterState (options) {
            options.forEach((opt) => {
                opt.classList.remove(ProductSwitcher.CSS.hidden);
                opt.setAttribute(ProductSwitcher.ATTRS.ariaHidden, 'false');
                delete opt.dataset.filteredProducts;
                delete opt.dataset.filteredLinks;
            });
        }

        _filterOptionAgainst (opt, allowedIds) {
            const ids = this._optionProducts(opt);
            const links = this._optionLinks(opt);
            const matches = ids.reduce(
                (acc, id, idx) => {
                    if (allowedIds.includes(id)) {
                        acc.ids.push(id);
                        acc.links.push(links[idx] || '');
                    }
                    return acc;
                },
                { ids: [], links: [] }
            );

            if (matches.ids.length === 0) {
                opt.classList.add(ProductSwitcher.CSS.hidden);
                opt.setAttribute(ProductSwitcher.ATTRS.ariaHidden, 'true');
                return;
            }
            opt.dataset.filteredProducts = matches.ids.join(',');
            opt.dataset.filteredLinks = matches.links.join(',');
        }

        /* ─── Click handling ────────────────────────────────────────────────── */

        _onOptionClick (event) {
            const option = event.currentTarget;
            const step = option.closest(ProductSwitcher.SELECTORS.step);
            const products = this._optionEffectiveProducts(option);
            const links = this._optionEffectiveLinks(option);
            const reload = this._reloadOnSelect(option, step);

            // If this click will navigate, do it IMMEDIATELY without any DOM mutation — any
            // interim update (is-selected toggle, applyFilter, reveal/hide blocks) would flash
            // briefly before the page loads. The current page must stay frozen until reload.
            const navigationUrl = this._resolveNavigationUrl(option, step, products, links, reload);
            if (navigationUrl) {
                window.location.href = navigationUrl;
                return;
            }

            // From here on, we're staying on the page — safe to mutate.
            this._selectOptionInStep(option, step);
            this._dispatchSelectEvent(option, step, products, links, reload);

            if (this._stepIndex(step) === ProductSwitcher.STEPS.gender) {
                this._applyStep2Filter();
                if (this._isConfigurator) this._revealNextSteps(ProductSwitcher.STEPS.gender);
            }

            if (this._isConfigurator) this._toggleConfiguratorBlocks(products, links);
        }

        _selectOptionInStep (option, step) {
            step.querySelectorAll(ProductSwitcher.SELECTORS.option)
                .forEach((opt) => opt.classList.remove(ProductSwitcher.CSS.selected));
            option.classList.add(ProductSwitcher.CSS.selected);
        }

        _dispatchSelectEvent (option, step, products, links, reload) {
            this.dispatchEvent(new CustomEvent(ProductSwitcher.EVENTS.select, {
                bubbles: true,
                detail: {
                    key: option.dataset.key,
                    products,
                    links,
                    reload,
                    stepIndex: step?.dataset.stepIndex
                }
            }));
        }

        _toggleConfiguratorBlocks (products, links) {
            const currentProductId = this._currentProductId();
            if (currentProductId && products.includes(currentProductId)) {
                this._revealHiddenBlocks();
                return;
            }
            if (!links.length) this._hideBlocks();
        }

        /* ─── Navigation resolution (no DOM side-effects) ───────────────────── */

        _resolveNavigationUrl (option, step, products, links, reload) {
            const currentProductId = this._currentProductId();
            const currentMatchesOption = currentProductId && products.includes(currentProductId);

            if (this._stepIndex(step) === ProductSwitcher.STEPS.gender) {
                const redirectLink = option.dataset.redirectLink;
                const isRedirectTarget = this._isRedirectTarget(option);
                if (redirectLink && !currentMatchesOption && !isRedirectTarget) return redirectLink;

                // Configurator mode + same product: the in-page "reveal hidden blocks"
                // path leaves the gallery stuck on its initial server-rendered subset of
                // product.media instead of the full variant_gallery set. Reload the
                // current product page (without #configurator) so the variant picker
                // re-boots on the same product, instead of jumping to links[0] — which
                // is the first product in the gender option, not necessarily this one.
                if (this._isConfigurator && (currentMatchesOption || isRedirectTarget)) {
                    return window.location.pathname;
                }
            }

            if (this._isConfigurator && !currentMatchesOption && links.length) {
                return links[0] + ProductSwitcher.CONFIGURATOR_HASH;
            }

            if (reload && links.length) return links[0];

            return null;
        }

        /* ─── Helpers ───────────────────────────────────────────────────────── */

        _findStep (index) {
            return this.querySelector(`${ProductSwitcher.SELECTORS.step}[${ProductSwitcher.ATTRS.stepIndex}="${index}"]`);
        }

        _stepsAfter (fromIndex) {
            return [...this._steps].filter((step) => this._stepIndex(step) > fromIndex);
        }

        _stepIndex (step) {
            return parseInt(step?.dataset.stepIndex, 10);
        }

        _reloadOnSelect (option, step) {
            return (option.dataset.reloadOnSelect || step?.dataset.reloadOnSelect) === 'true';
        }

        _optionProducts (opt) {
            return this._csvToArray(opt.dataset.products);
        }

        _optionLinks (opt) {
            return this._csvToArray(opt.dataset.links);
        }

        _optionEffectiveProducts (opt) {
            return this._csvToArray(opt.dataset.filteredProducts || opt.dataset.products);
        }

        _optionEffectiveLinks (opt) {
            return this._csvToArray(opt.dataset.filteredLinks || opt.dataset.links);
        }

        // The current page is this option's redirect target (e.g. an "anciennes tailles" product
        // that isn't listed in the option's products).
        _isRedirectTarget (opt) {
            return Boolean(opt.dataset.redirectLink) && opt.dataset.redirectLink === window.location.pathname;
        }

        _csvToArray (csv) {
            if (!csv || typeof csv !== 'string') return [];
            return csv.split(',').map((s) => s.trim()).filter(Boolean);
        }

        _currentProductId () {
            return this.dataset.currentProductId ||
                (window?.ShopifyAnalytics?.meta?.product?.id
                    ? String(window.ShopifyAnalytics.meta.product.id)
                    : null);
        }
    }

    customElements.define('product-switcher', ProductSwitcher);
}
